/**
 * Pricing derived from VMK / Config Go quotation PDFs:
 *
 * - 20260609110503987testkozijnenprijsv1.pdf — tilt-turn, hopper, achterdeur, baseline doors
 * - Config go prijzen kozijnen vast.pdf — fixed kozijnen (1 & 2 parts), ventilator, glass texture
 * - prijzen Config go 3.0.pdf — multi-part kozijnen (2–4 parts), DK / hopper / sandwich panel
 * - Prijzen tuindeuren en achterdeuren config go.pdf — terrasdeur & achterdeur layouts (24 mm)
 * - 20260612092707603w / 26212w / 93243382w / 92641128w — 36 mm single & mixed window lines
 * - config go prijzen deuren.pdf — voordeur (doors — separate from kozijnen pricing)
 *
 * Glazing codes on quote lines:
 * - HR++ - 5-15-4*  → Double Glass 24 mm (HR++) — baseline for all PDF formulas
 * - HR+++ - 4-12-4-12-4* → Triple Glass 36 mm (HR+++) — dedicated PDF formulas below
 */

import { resolveSidePanelWidth, countAchterdeurTwoPartsParapetSections, calcTerrasdeurSandwichParapetPremium, calcTerrasdeurSidePanelOverheadPremium, DOOR_SANDWICH_SECTION_PREMIUM } from './voordeurConfigHelpers';

const GLAZING_36_IDS = new Set(['triple', 'double-36']);
const isGlazing36mm = (glazingTypeId) => GLAZING_36_IDS.has(glazingTypeId);

const FIXED_FRAME_OFFSET_MM = 140;
const OPENING_FRAME_OFFSET_MM = 240;
const LOW_HEIGHT_THRESHOLD_MM = 600;
const MULTI_PART_GLASS_WIDTH_OFFSET_MM = 96;

/**
 * Reference quotation PDF — fixed sash with VensterbANK (window sill).
 * Conf Go kozijnen vast list is without vensterbank.
 */

const REFERENCE_FIXED_WINDOW = {
  intercept: 196.4833,
  glassAreaRate: 160.3584,
  lowHeightRate: 74.8396,
};

/** Config Go prijzen kozijnen vast.pdf — fixed sash without vensterbank / ramp dorpel */

const CONFI_GO_FIXED_WINDOW = {
  intercept: 66.116,
  glassAreaRate: 246.3953,
  mullionPremium: 34.3788,
  ventPremiumSinglePart: 81.57,
  ventPremiumMultiPart: 81.305,
  texturedGlassPremium: 76.33,
};

/** Tilt-turn (DK vleugel) and hopper / tilt-only (valraam) — reference PDF */

const OPENING_WINDOW = {
  tiltIntercept: 304.1029,
  glassAreaRate: 250.4092,
  hopperPremium: 17.6004,
};

/** Voordeur & achterdeur area base — legacy reference PDF (single-part doors) */

const DOOR = {
  intercept: 1231.95,
  areaRate: 95.75,
};

/**
 * Plain tuindeur / achterdeur (no side panels, normal, no boswering).
 * Prijzen tuindeuren en achterdeuren config go.pdf items A & N.
 */

const PLAIN_TUINDEUR = {
  intercept: -208.47,
  areaRate: 728.58636364,
};

/**
 * CONFIG-GO TUINDUEREN / ACHTERDEUREN — decomposed premiums from PDF items A–N.
 *
 * price = plainDoor(doorSectionW × doorSectionH)
 *       + parapetPremium
 *       + firstSideGlassPremium + additionalSidePremium
 *       + sideSandwichPremium
 *       + topLightPremium
 *       + outerHeightBand (achterdeur both-panels + top light only)
 *
 * Constants are differences between paired PDF quotation lines (see docs/pdf-extract/).
 */

const TUINDEUR_DOOR_REF_WIDTH_MM = { achterdeur: 1000, terrasdeur: 2000 };
const TUINDEUR_REF_DOOR_HEIGHT_MM = 2200;

/** PDF I − N @ 1000×2200 — T-profiel + sandwich lower section */

const TUINDEUR_PARAPET_PREMIUM_AT_REF = 102;

/** PDF H − G @ 2200×2200 — main-door parapet with one glass side panel */

const TUINDEUR_PARAPET_WITH_SIDE_AT_REF = 207.19;
const TUINDEUR_PARAPET_WITH_SIDE_DOOR_REF = { w: 2000, h: 2200 };

/** PDF K − N (achterdeur) and PDF G − A (terrasdeur) — first glass side panel €/mm @ 2200 */

const TUINDEUR_FIRST_SIDE_GLASS_MM_RATE = {
  achterdeur: 343.24 / 300,
  terrasdeur: (3226.07 - 2997.31) / 200,
};

/** PDF C − G — each extra side width beyond the first (400 mm @ 2200 mm) */

const TUINDEUR_ADDITIONAL_SIDE_MM_RATE = (3556 - 3226.07) / 400;

/** PDF B − G — sandwich plint in a side panel (200 mm @ 2200 mm) */

const TUINDEUR_SIDE_SANDWICH_MM_RATE = (3332.49 - 3226.07) / 200;

/** Terrasdeur both-panels PDF C — left side is ⅓ of side width, right is ⅔ */

const TUINDEUR_TERRAS_BOTH_SIDE_LEFT_RATIO = 200 / 600;

/**
 * Top-light band premiums from paired PDF lines:
 * D−A, J−I, L−K, E−(plain+side), F−C
 */

const TUINDEUR_TOP_LIGHT = {
  plain: { refWidth: 2000, premium: 342.88 },
  parapet: { refWidth: 1000, premium: 188.27 },
  withSide: { refWidth: 1300, premium: 214.43 },
  withSideTerras: { refWidth: 2300, premium: 289.68 },
  bothSides: { refWidth: 2600, premium: 364.07 },
};

/** @deprecated Layout keys kept for tests / debugging only */

const TUINDEUR_PROFILE_ANCHORS = {
  plain: [
    { width: 1000, height: 2200, price: 1394.42 },
    { width: 2000, height: 2200, price: 2997.31 },
  ],
  'plain-parapet': [{ width: 1000, height: 2200, price: 1496.42 }],
  'plain-top': [{ width: 2000, height: 2500, price: 3340.19 }],
  'plain-top-parapet': [{ width: 1000, height: 2500, price: 1684.69 }],
  left: [
    { width: 1300, height: 2200, price: 1737.66 },
    { width: 2200, height: 2200, price: 3226.07 },
  ],
  'left-top': [
    { width: 1300, height: 2500, price: 1952.09 },
    { width: 2300, height: 2500, price: 3630.13 },
  ],
  'left-sandwich': [{ width: 2200, height: 2200, price: 3332.49 }],
  'both-glass': [{ width: 2600, height: 2200, price: 3556.0 }],
  'both-top': [
    { width: 1600, height: 2500, price: 2583.8 },
    { width: 2600, height: 2500, price: 3920.07 },
  ],
  'both-parapet': [{ width: 2200, height: 2200, price: 3433.26 }],
};

/** Paired normal ↔ bovenlicht PDF lines — premium scaled by width & overhead height */

const TUINDEUR_TOP_PROFILE_FOR = {
  plain: 'plain-top',
  'plain-parapet': 'plain-top-parapet',
  left: 'left-top',
  'both-glass': 'both-top',
};

const TUINDEUR_TOP_LIGHT_PREMIUM = {
  plain: { refWidth: 2000, premium: 342.88 },
  'plain-parapet': { refWidth: 1000, premium: 188.27 },
  left: { refWidth: 1300, premium: 214.43 },
  'both-glass': { refWidth: 2600, premium: 364.07 },
};

/**
 * CONFIG-GO VOORDEUR — composed premiums from Prijzen Deuren config go.pdf items A–N.
 *
 * PDF line letters are quote sequence IDs, NOT door-style numbers.
 * Door styles 1–6 map to Config Go thumbnails; see VOORDEUR_PDF_REFERENCE_LINES.
 *
 * price = style4Plain(doorW × doorH)
 *       + stylePremium (styles 1–3, 5–6 vs style-4 @ 1000×2200)
 *       + style-1 fourPartsPremium + standardKnobPremium (PDF D)
 *       + style-3 glassPartsPremium (PDF N)
 *       + style-6 layout premium K vs M (PDF K / M)
 *       + firstSideGlass + additionalSideGlass
 *       + topLight (dedicated linear fits for PDF G / H / I)
 */

/**
 * Authoritative Config Go → Jopp config mapping (Prijzen Deuren config go.pdf).
 * Documentation / test fixtures only — pricing never looks up this table.
 * All prices come from calcVoordeurComposedPrice() + calcVoordeurTopLightPrice().
 * @type {ReadonlyArray<{ pdfLine: string, price: number, doorStyle: string, designType: string, doorType: string, width: number, height: number, numberOfParts?: number, parapet?: string, leftPanelWidth?: number, rightPanelWidth?: number, style6Layout?: 'k' | 'm', notes?: string }>}
 */

export const VOORDEUR_PDF_REFERENCE_LINES = [
  { pdfLine: 'A', price: 1471.78, doorStyle: 'style-4', designType: 'no-panel', doorType: 'normal', width: 1000, height: 2200 },
  { pdfLine: 'B', price: 1476.38, doorStyle: 'style-4', designType: 'no-panel', doorType: 'normal', width: 1010, height: 2200 },
  { pdfLine: 'C', price: 1478.69, doorStyle: 'style-4', designType: 'no-panel', doorType: 'normal', width: 1010, height: 2210 },
  { pdfLine: 'D', price: 1934.99, doorStyle: 'style-1', designType: 'no-panel', doorType: 'normal', width: 1000, height: 2200, numberOfParts: 4, parapet: 'standard-knob', notes: 'Style 1, 4 parts + Standaard Deurkruk binnen' },
  { pdfLine: 'E', price: 1543.66, doorStyle: 'style-4', designType: 'left-panel', doorType: 'normal', width: 1300, height: 2200, notes: '300 mm glass side panel' },
  { pdfLine: 'F', price: 1818.78, doorStyle: 'style-4', designType: 'both-panels', doorType: 'normal', width: 1500, height: 2200, leftPanelWidth: 300, rightPanelWidth: 300, notes: 'Both panels; drawing 300+900+300 — Config Go prices door leaf @ 1000 mm + 500 mm total side (buitenmaat − 1000)' },
  { pdfLine: 'G', price: 1503.69, doorStyle: 'style-4', designType: 'no-panel', doorType: 'top-light', width: 1000, height: 2500 },
  { pdfLine: 'H', price: 1865.01, doorStyle: 'style-4', designType: 'left-panel', doorType: 'top-light', width: 1300, height: 2500 },
  { pdfLine: 'I', price: 2152.47, doorStyle: 'style-4', designType: 'both-panels', doorType: 'top-light', width: 1500, height: 2500, leftPanelWidth: 500, rightPanelWidth: 0 },
  { pdfLine: 'J', price: 1625.04, doorStyle: 'style-5', designType: 'no-panel', doorType: 'normal', width: 1000, height: 2200, notes: 'Brievenbusstijl transom' },
  { pdfLine: 'K', price: 1894.93, doorStyle: 'style-6', designType: 'no-panel', doorType: 'normal', width: 1000, height: 2200, style6Layout: 'k', notes: 'Style 6, 4-part band layout (PDF K)' },
  { pdfLine: 'L', price: 1315.34, doorStyle: 'style-2', designType: 'no-panel', doorType: 'normal', width: 1000, height: 2200, notes: 'Full-height glass' },
  { pdfLine: 'M', price: 1573.39, doorStyle: 'style-6', designType: 'no-panel', doorType: 'normal', width: 1000, height: 2200, style6Layout: 'm', notes: 'Style 6, three horizontal glass bands' },
  { pdfLine: 'N', price: 1810.57, doorStyle: 'style-3', designType: 'no-panel', doorType: 'normal', width: 1000, height: 2200, numberOfParts: 4, notes: 'Style 3, glass parts' },
];
const VOORDEUR_DOOR_REF_WIDTH_MM = 1000;
const VOORDEUR_REF_DOOR_HEIGHT_MM = 2200;
/**
 * Flat toeslag HVL (Config Go “Toeslag HVL” under Extra's).
 * PDF unit prices A–N include this when HVL is selected; amount is not broken out in the PDF text.
 * Calibrate from a paired Config Go quote (same config, Verstek vs HVL) when available.
 */

/** Flat HVL premium per door drawing (incl. 21% BTW catalog amount). */
export const DOOR_HVL_SURCHARGE_INCL_VAT = 350;
export const VOORDEUR_HVL_SURCHARGE = DOOR_HVL_SURCHARGE_INCL_VAT;

/**
 * Pricing recreation starting point: flat door base price (catalog incl. 21% BTW).
 * Ex-VAT amounts are returned from calcVoordeurPrice / calcAchterdeurPrice / calcTerrasdeurPrice.
 */
const VOORDEUR_FLAT_BASE_PRICE = 5000;

/** Achterdeur recreation base — same layout premiums as voordeur, different flat base (catalog incl. BTW). */
const ACHTERDEUR_FLAT_BASE_PRICE = 3700;

/** Terrasdeur recreation base — same layout premiums as voordeur, different flat base (catalog incl. BTW). */
const TERRASDEUR_FLAT_BASE_PRICE = 7500;

/** Top-light height tiers (mm) — no side panel */
const VOORDEUR_TOP_LIGHT_TIERS = [
  { min: 300, max: 500, premium: 500 },
  { min: 501, max: 950, premium: 750 },
];

/** Side-panel width tiers (mm) — per panel; both-panels applies the tier to left and right separately */
const VOORDEUR_SIDE_PANEL_TIERS = [
  { min: 300, max: 500, premium: 600 },
  { min: 501, max: 1000, premium: 1100 },
  { min: 1001, max: 1950, premium: 2000 },
];

/** Extra surcharge when both side panel and top light are present and overall width > 1500 mm */
const VOORDEUR_COMBO_WIDE_WIDTH_MM = 1500;
const VOORDEUR_COMBO_SURCHARGE = 250;

/** Tilt-turn / tilt-only on side panel and/or top light (fixed types are excluded) */
const VOORDEUR_OPENING_WINDOW_PREMIUM = 300;
const VOORDEUR_OPENING_WINDOW_BOTH_ZONES_PREMIUM = 600;

const lookupTierPremium = (valueMm, tiers) => {
  const value = Number(valueMm) || 0;
  if (value <= 0) return 0;
  for (const tier of tiers) {
    if (value >= tier.min && value <= tier.max) return tier.premium;
  }
  if (value < tiers[0].min) return 0;
  return tiers[tiers.length - 1].premium;
};

/** PDF E − A — first 300 mm glass side panel @ 2200 (legacy reference; superseded by tier table) */

const VOORDEUR_FIRST_SIDE_REF_MM = 300;
const VOORDEUR_FIRST_SIDE_PREMIUM_AT_REF = 71.88;
/** PDF F − E — extra side width beyond the first 300 mm */

const VOORDEUR_ADDITIONAL_SIDE_MM_RATE = (1818.78 - 1543.66) / 200;
/** PDF D − A — style-1 with 4 parts */

const VOORDEUR_STYLE1_FOUR_PARTS_PREMIUM = 338.79;
/** PDF D − N — style-1, 4 parts + Standaard Deurkruk binnen (parapet: standard-knob) */

const VOORDEUR_STANDARD_KNOB_PREMIUM = 124.42;
/** PDF N − A — style-3 glass parts */

const VOORDEUR_STYLE3_GLASS_PARTS_PREMIUM = 338.79;
/** PDF M / K − A — style-6 layout premiums @ 1000×2200 */

const VOORDEUR_STYLE6_M_PREMIUM_AT_REF = 101.61;
const VOORDEUR_STYLE6_K_PREMIUM_AT_REF = 423.15;
/**
 * Style premiums over style-4 base @ 1000×2200 — PDF J, L.
 * Style-6 uses style6Layout; style-1/3 use part premiums above.
 */

const VOORDEUR_STYLE_PREMIUM_AT_REF = {
  'style-1': 0,
  'style-2': -156.44,
  'style-3': 0,
  'style-4': 0,
  'style-5': 153.26,
  'style-6': null,
};

/**
 * 36 mm opening windows — 20260612092641128-merged.pdf items C, D (tilt-turn) + A (hopper).
 */

const OPENING_WINDOW_36 = {
  intercept: 305.33,
  glassAreaRate: 218.76,
  hopperPremium: 23.8,
  smallHopperGlassAreaThreshold: 0.02,
  smallHopperUnitPrice: 269.0,
};

/**
 * 36 mm fixed kozijnen uplift per m² glass — merged PDF item E (1000×1576).
 * Premium over Config Go 24 mm base at same dimensions.
 */

const GLAZING_36_FIXED_PREMIUM_PER_SQM = 48.49;

/**
 * 36 mm mixed 2-part (fixed + DK) — merged PDF items F, G, H (I = F).
 * Interpolate on outer frame area (m²).
 */

const MIXED_2PART_KOZIJNEN_36_ANCHORS = [
  { outerAreaSqm: 2.21004, price: 779.55 },
  { outerAreaSqm: 2.89556, price: 920.14 },
  { outerAreaSqm: 4.39375, price: 1236.07 },
];

/** Merged PDF item J — achterdeur 918×2164 flat adjustment over 24 mm door formula */

const ACHTERDEUR_36_ADJUSTMENT = 6.81;

/** Merged PDF item K — voordeur 945×2461 BUVA 55 (36 mm quote line) */

const VOORDEUR_36_K_ANCHOR = { width: 945, height: 2461, price: 1489.64 };

/** style-1 + no-panel + top-light — PDF item G */

const VOORDEUR_LEFT_PANEL_TOP = {
  widthRate: 1.43729102,
  heightRate: -0.001392,
  areaRate: 0.00359322754,
};

/** no-panel + style-1 + top-light — PDF item G */

const VOORDEUR_NO_PANEL_TOP = {
  widthRate: 0.20740531,
  heightRate: 0.518513275,
  areaRate: 0.000518513275,
};

const TEXTURED_GLASS_IDS = new Set(['chinchilla', 'satinato']);

/**
 * Configurator options not yet covered by a PDF price list.
 */

export const UNPRICED_OPTIONS = [
  'Windows with 5 parts where layout is not all fixed glass',
  '36 mm kozijnen with 3+ mixed parts (only 2-part mixed has PDF anchors)',
  'Ramp dorpel (30 / 45 / 60 mm)',
  'Vensterbank sizes other than 30 mm (until dedicated PDF lines)',
  'Window type: Fixed Panel (vast paneel) on single-part kozijnen',
  'Overhead (bovenlicht)',
  'Underlight (onderlicht)',
  'Division type (equal / divide)',
  'Profile system (Veka Softline 82 NL / Schüco variants)',
  'Wood joint 90° / HVL on kozijnen (PDF kozijnen use 15° blokkader)',
  'Frame colour surcharges (PDF uses fixed RAL combinations)',
  'Interior frame colour surcharges',
  'Hardware & accessories beyond PDF samples',
  'Screen / roller shutter / HOR / buoy sharing',
  'Schuifpui (sliding door)',
  'Terrasdeur / achterdeur 36 mm glazing (PDF lines are 24 mm HR++ only)',
  'Terrasdeur / achterdeur chinchilla / satinato glass texture',
  'Terrasdeur right-panel (priced same as left-panel mirror)',
  'Threshold height 55mm (PDF uses BUVA 55mm dorpel)',
  'Security / SKG options beyond PDF sample',
  'Terrasdeur lintels / overhead parts count beyond PDF samples',
];

const round2 = (value) => Math.round(value * 100) / 100;

/** Dutch BTW — applied on quote export; door configurator catalog amounts are stored ex-VAT. */
export const BTW_RATE = 0.21;
export const BTW_MULTIPLIER_INCL = 1 + BTW_RATE;

/** Removal of old frames / container — once per quote, bundled in investment total (ex VAT). */
export const CONTAINER_REMOVAL_FEE_EX_VAT = 500;

/** Container removal fee — only when client opted in on the quote form. */
export const getQuoteProjectFeesExVat = (clientDetails = null) =>
  clientDetails?.container === true ? CONTAINER_REMOVAL_FEE_EX_VAT : 0;

/**
 * Door configurators (voordeur, achterdeur, terrasdeur, schuifpui) were priced incl. 21% BTW.
 * Convert catalog totals to ex-VAT so export preview can add BTW and match the original incl. total.
 */
export const normalizeDoorCatalogPriceToExVat = (priceInclVat) =>
  round2(Number(priceInclVat) / BTW_MULTIPLIER_INCL);

export const addBtwToExVatPrice = (priceExVat) =>
  round2(Number(priceExVat) * BTW_MULTIPLIER_INCL);

/** Kozijnen (windows) — area-based pricing from overall width × height (catalog amounts incl. 21% BTW). */
export const KOZIJNEN_MIN_DIMENSION_MM = 300;
export const KOZIJNEN_REFERENCE_AREA_SQM = 1; // 1000 × 1000 mm
export const KOZIJNEN_BASE_PRICE_INCL_VAT = 2000;
export const KOZIJNEN_AREA_INCREMENT_SQM = 0.1;
export const KOZIJNEN_AREA_INCREMENT_PRICE_INCL_VAT = 33;

export const KOZIJNEN_VENTILATOR_BASE_PRICE_INCL_VAT = 150;
export const KOZIJNEN_VENTILATOR_WIDTH_REFERENCE_MM = 1000;
export const KOZIJNEN_VENTILATOR_WIDTH_INCREMENT_MM = 100;
export const KOZIJNEN_VENTILATOR_WIDTH_INCREMENT_PRICE_INCL_VAT = 10;

export const KOZIJNEN_OVERHEAD_PRICE_PER_PART_INCL_VAT = 75;
export const KOZIJNEN_UNDERLIGHT_PRICE_PER_PART_INCL_VAT = 75;
export const KOZIJNEN_VENSTERBANK_PRICE_INCL_VAT = 75;
export const KOZIJNEN_OPENING_MECHANISM_PRICE_PER_PART_INCL_VAT = 350;
export const KOZIJNEN_HOR_PRICE_PER_OPENING_PART_INCL_VAT = 60;
export const KOZIJNEN_HVL_SURCHARGE_INCL_VAT = 350;
export const KOZIJNEN_RAMP_DORPEL_30MM_PRICE_PER_METER_EX_VAT = 100;
export const KOZIJNEN_GLAZING_36MM_PRICE_PER_SQM_EX_VAT = 100;
export const KOZIJNEN_TEXTURED_GLASS_PRICE_PER_SQM_EX_VAT = 100;
export const ELECTRIC_SCREEN_BASE_EX_VAT = 1100;
export const ELECTRIC_SCREEN_INCREMENT_PER_50_SQM_EX_VAT = 500;
export const ELECTRIC_ROLLER_SHUTTER_BASE_EX_VAT = 900;
export const ELECTRIC_ROLLER_SHUTTER_INCREMENT_PER_50_SQM_EX_VAT = 500;

export const calcKozijnenAreaSqm = (widthMm, heightMm) =>
  (Math.max(0, Number(widthMm) || 0) * Math.max(0, Number(heightMm) || 0)) / 1_000_000;

/**
 * Up to 1 m² (e.g. 1000×1000 mm): €2000 incl. BTW.
 * Each additional 0.1 m² adds €33 incl. BTW.
 * Ventilator surcharges are added per sash when selected.
 * Returned unit price is ex-VAT so Summary / quote export can add 21% BTW.
 */
export const calcKozijnenVentilatorUnitPriceInclVat = (widthMm) => {
  const width = Math.max(0, Number(widthMm) || 0);
  if (width <= 0) return 0;

  let price = KOZIJNEN_VENTILATOR_BASE_PRICE_INCL_VAT;
  if (width > KOZIJNEN_VENTILATOR_WIDTH_REFERENCE_MM) {
    const extraWidth = width - KOZIJNEN_VENTILATOR_WIDTH_REFERENCE_MM;
    price +=
      (extraWidth / KOZIJNEN_VENTILATOR_WIDTH_INCREMENT_MM) *
      KOZIJNEN_VENTILATOR_WIDTH_INCREMENT_PRICE_INCL_VAT;
  }

  return round2(price);
};

const hasKozijnenOverhead = (config) =>
  config.overhead === 'overhead' ||
  (Array.isArray(config.overheadPerPart) && config.overheadPerPart.some(Boolean));

const hasKozijnenUnderlight = (config) =>
  config.underlight === 'underlight' ||
  (Array.isArray(config.underlightPerPart) && config.underlightPerPart.some(Boolean));

const sumKozijnenVentilatorRow = (ventFlags, partWidths, parts) => {
  let total = 0;
  for (let i = 0; i < parts; i += 1) {
    if (ventFlags[i]) {
      total += calcKozijnenVentilatorUnitPriceInclVat(partWidths[i]);
    }
  }
  return total;
};

/** Total ventilator surcharge (incl. BTW) across main, overhead, and underlight sashes. */
export const calcKozijnenVentilatorTotalInclVat = (config) => {
  const totalWidth = Number(config.width) || 0;
  if (totalWidth <= 0) return 0;

  const parts = Number(config.parts) || 1;
  const mainWidths = getPartWidths(config, parts, totalWidth);
  let total = sumKozijnenVentilatorRow(config.ventilatorParts || [], mainWidths, parts);

  if (hasKozijnenOverhead(config)) {
    const overheadParts = Number(config.overheadParts) || 1;
    const overheadWidths = getPartWidths(
      { partWidths: config.overheadPartWidths },
      overheadParts,
      totalWidth
    );
    total += sumKozijnenVentilatorRow(
      config.ventilatorOverheadParts || [],
      overheadWidths,
      overheadParts
    );
  }

  if (hasKozijnenUnderlight(config)) {
    const underlightParts = Number(config.underlightParts) || 1;
    const underlightWidths = getPartWidths(
      { partWidths: config.underlightPartWidths },
      underlightParts,
      totalWidth
    );
    total += sumKozijnenVentilatorRow(
      config.ventilatorUnderlightParts || [],
      underlightWidths,
      underlightParts
    );
  }

  return round2(total);
};

/** Number of overhead sashes — global overhead parts or per-main-part overhead flags. */
export const countKozijnenOverheadParts = (config) => {
  if (!hasKozijnenOverhead(config)) return 0;

  if (config.overhead === 'overhead') {
    return Math.max(1, Number(config.overheadParts) || 1);
  }

  return (config.overheadPerPart || []).filter(Boolean).length;
};

/** Overhead surcharge (incl. BTW): €75 per overhead part. */
export const calcKozijnenOverheadTotalInclVat = (config) => {
  const partCount = countKozijnenOverheadParts(config);
  if (partCount <= 0) return 0;
  return round2(partCount * KOZIJNEN_OVERHEAD_PRICE_PER_PART_INCL_VAT);
};

/** Number of underlight sashes when underlight is enabled. */
export const countKozijnenUnderlightParts = (config) => {
  if (!hasKozijnenUnderlight(config)) return 0;

  if (config.underlight === 'underlight') {
    return Math.max(1, Number(config.underlightParts) || 1);
  }

  return (config.underlightPerPart || []).filter(Boolean).length;
};

/** Underlight surcharge (incl. BTW): €75 per underlight part. */
export const calcKozijnenUnderlightTotalInclVat = (config) => {
  const partCount = countKozijnenUnderlightParts(config);
  if (partCount <= 0) return 0;
  return round2(partCount * KOZIJNEN_UNDERLIGHT_PRICE_PER_PART_INCL_VAT);
};

/** Vensterbank surcharge (incl. BTW): €75 when 30 mm window sill is selected. */
export const calcKozijnenVensterbankTotalInclVat = (config) => {
  if (!config?.vensterbank || config.vensterbank === 'no-vensterbank') return 0;
  return KOZIJNEN_VENSTERBANK_PRICE_INCL_VAT;
};

/**
 * Main-frame parts with tilt-turn or tilt-only (not fixed sash / glass / panel).
 * €350 incl. BTW per qualifying part.
 */
export const countKozijnenOpeningMechanismParts = (config) => {
  const parts = Number(config.parts) || 1;
  const types = config.partWindowTypes || [];
  let count = 0;
  for (let i = 0; i < parts; i += 1) {
    const type = types[i] ?? types[0] ?? 'fixed-sash';
    if (isOpeningWindowType(type)) {
      count += 1;
    }
  }
  return count;
};

export const calcKozijnenOpeningMechanismTotalInclVat = (config) => {
  const partCount = countKozijnenOpeningMechanismParts(config);
  if (partCount <= 0) return 0;
  return round2(partCount * KOZIJNEN_OPENING_MECHANISM_PRICE_PER_PART_INCL_VAT);
};

/** Hor (incl. BTW): €60 per main part with opening, only when Hor is enabled. */
export const calcKozijnenHorTotalInclVat = (config) => {
  if (!config?.hor) return 0;
  const openingPartCount = countKozijnenOpeningMechanismParts(config);
  if (openingPartCount <= 0) return 0;
  return round2(openingPartCount * KOZIJNEN_HOR_PRICE_PER_OPENING_PART_INCL_VAT);
};

export const kozijnenUsesHvl = (config) =>
  config?.woodJoint === 'joint-90' || config?.hvlSurcharge === true;

export const calcKozijnenHvlTotalInclVat = (config) =>
  kozijnenUsesHvl(config) ? KOZIJNEN_HVL_SURCHARGE_INCL_VAT : 0;

export const calcKozijnenRampDorpelExVat = (config) => {
  if (config?.rampDorpel !== 'ramp-dorpel-30mm') return 0;
  const widthMm = Number(config?.width) || 0;
  if (widthMm <= 0) return 0;
  return round2((widthMm / 1000) * KOZIJNEN_RAMP_DORPEL_30MM_PRICE_PER_METER_EX_VAT);
};

export const calcKozijnenGlazing36mmExVat = (config, pageType = 'kozijnen') => {
  if (!isGlazing36mm(config?.glazingType)) return 0;
  const glassArea = getConfiguratorGlazingAreaSqm(config, pageType);
  return round2(glassArea * KOZIJNEN_GLAZING_36MM_PRICE_PER_SQM_EX_VAT);
};

export const calcKozijnenTexturedGlassExVat = (config, pageType = 'kozijnen') => {
  const texture = config?.glassTexture;
  if (texture !== 'chinchilla' && texture !== 'satinato') return 0;
  const glassArea = getConfiguratorGlazingAreaSqm(config, pageType);
  return round2(glassArea * KOZIJNEN_TEXTURED_GLASS_PRICE_PER_SQM_EX_VAT);
};

const calcAreaTieredElectricSurchargeExVat = (areaSqm, baseExVat, incrementPer50SqmExVat) => {
  const area = Math.max(0, Number(areaSqm) || 0);
  const tiers = Math.floor(area / 50);
  return round2(baseExVat + tiers * incrementPer50SqmExVat);
};

export const calcElectricScreenPriceExVat = (config) => {
  if (config?.screenElectricallyOperated !== true) return 0;
  const areaSqm = calcKozijnenAreaSqm(config?.width, config?.height);
  return calcAreaTieredElectricSurchargeExVat(
    areaSqm,
    ELECTRIC_SCREEN_BASE_EX_VAT,
    ELECTRIC_SCREEN_INCREMENT_PER_50_SQM_EX_VAT
  );
};

export const calcElectricRollerShutterPriceExVat = (config) => {
  if (config?.rollerShutterElectricallyOperated !== true) return 0;
  const areaSqm = calcKozijnenAreaSqm(config?.width, config?.height);
  return calcAreaTieredElectricSurchargeExVat(
    areaSqm,
    ELECTRIC_ROLLER_SHUTTER_BASE_EX_VAT,
    ELECTRIC_ROLLER_SHUTTER_INCREMENT_PER_50_SQM_EX_VAT
  );
};

export const calcKozijnenOptionSurchargesExVat = (config) =>
  round2(
    calcKozijnenRampDorpelExVat(config) +
      calcKozijnenGlazing36mmExVat(config, 'kozijnen') +
      calcKozijnenTexturedGlassExVat(config, 'kozijnen') +
      calcElectricScreenPriceExVat(config) +
      calcElectricRollerShutterPriceExVat(config)
  );

export const calcKozijnenAreaPrice = (widthMm, heightMm, config = null) => {
  const width = Number(widthMm) || 0;
  const height = Number(heightMm) || 0;

  if (width < KOZIJNEN_MIN_DIMENSION_MM || height < KOZIJNEN_MIN_DIMENSION_MM) {
    return { price: 0, hasPricing: false, reason: 'below-min-dimensions' };
  }

  const areaSqm = calcKozijnenAreaSqm(width, height);
  const extraArea = Math.max(0, areaSqm - KOZIJNEN_REFERENCE_AREA_SQM);
  const areaPriceInclVat = round2(
    KOZIJNEN_BASE_PRICE_INCL_VAT +
      (extraArea / KOZIJNEN_AREA_INCREMENT_SQM) * KOZIJNEN_AREA_INCREMENT_PRICE_INCL_VAT
  );
  const ventilatorInclVat = config ? calcKozijnenVentilatorTotalInclVat(config) : 0;
  const overheadInclVat = config ? calcKozijnenOverheadTotalInclVat(config) : 0;
  const underlightInclVat = config ? calcKozijnenUnderlightTotalInclVat(config) : 0;
  const vensterbankInclVat = config ? calcKozijnenVensterbankTotalInclVat(config) : 0;
  const openingMechanismInclVat = config ? calcKozijnenOpeningMechanismTotalInclVat(config) : 0;
  const horInclVat = config ? calcKozijnenHorTotalInclVat(config) : 0;
  const hvlInclVat = config ? calcKozijnenHvlTotalInclVat(config) : 0;
  const priceInclVat = round2(
    areaPriceInclVat +
      ventilatorInclVat +
      overheadInclVat +
      underlightInclVat +
      vensterbankInclVat +
      openingMechanismInclVat +
      horInclVat +
      hvlInclVat
  );
  const optionSurchargesExVat = config ? calcKozijnenOptionSurchargesExVat(config) : 0;

  return {
    price: round2(normalizeDoorCatalogPriceToExVat(priceInclVat) + optionSurchargesExVat),
    hasPricing: true,
  };
};

const glassAreaSqm = (widthMm, heightMm, frameOffsetMm) => {
  const glassW = Math.max(0, widthMm - frameOffsetMm);
  const glassH = Math.max(0, heightMm - frameOffsetMm);
  return (glassW * glassH) / 1_000_000;
};

const isFixedWindowType = (type) =>
  type === 'fixed-sash' || type === 'fixed-glass';

const interpolateAnchors = (width, anchors) => {
  const sorted = [...anchors].sort((a, b) => a.width - b.width);
  if (width <= sorted[0].width) {
    const [a, b] = [sorted[0], sorted[1]];
    const slope = (b.price - a.price) / (b.width - a.width);
    return a.price + slope * (width - a.width);
  }
  if (width >= sorted[sorted.length - 1].width) {
    const [a, b] = [sorted[sorted.length - 2], sorted[sorted.length - 1]];
    const slope = (b.price - a.price) / (b.width - a.width);
    return b.price + slope * (width - b.width);
  }
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const left = sorted[i];
    const right = sorted[i + 1];
    if (width >= left.width && width <= right.width) {
      const ratio = (width - left.width) / (right.width - left.width);
      return left.price + ratio * (right.price - left.price);
    }
  }
  return sorted[0].price;
};

const getPartWidths = (config, parts, totalWidth) => {
  if (Array.isArray(config.partWidths) && config.partWidths.length === parts) {
    const sum = config.partWidths.reduce((acc, value) => acc + Number(value), 0);
    if (sum > 0) return config.partWidths.map((value) => Number(value));
  }
  const base = Math.floor(totalWidth / parts);
  const widths = Array(parts).fill(base);
  widths[parts - 1] += totalWidth - base * parts;
  return widths;
};

const countFixedVentilators = (config, parts) => {
  const ventilatorParts = config.ventilatorParts || [];
  const partTypes = config.partWindowTypes || [];
  let count = 0;
  for (let i = 0; i < parts; i += 1) {
    if (ventilatorParts[i] && isFixedWindowType(partTypes[i] || 'fixed-sash')) {
      count += 1;
    }
  }
  return count;
};

const hasTexturedGlass = (config) =>
  TEXTURED_GLASS_IDS.has(config.glassTexture);

/** Vensterbank (window sill) — separate from ramp dorpel; drives reference fixed pricing. */

const hasVensterbank = (config) =>
  Boolean(config?.vensterbank && config.vensterbank !== 'no-vensterbank');

const calcConfiGoFixedGlassArea = (partWidths, heightMm, parts = 1) => {
  if (parts === 1) {
    return glassAreaSqm(partWidths[0], heightMm, FIXED_FRAME_OFFSET_MM);
  }

  return partWidths.reduce((total, partWidth) => {
    const glassW = Math.max(0, partWidth - MULTI_PART_GLASS_WIDTH_OFFSET_MM);
    const glassH = Math.max(0, heightMm - FIXED_FRAME_OFFSET_MM);
    return total + (glassW * glassH) / 1_000_000;
  }, 0);
};

/** Glazing pane area (m²) — aligned with VMK quote HR++ row dimensions. */

export const getConfiguratorGlazingAreaSqm = (config, pageType) => {
  const width = Number(config?.width) || 0;
  const height = Number(config?.height) || 0;
  if (width <= 0 || height <= 0) return 0;

  const isDoor =
    pageType === 'voordeur' ||
    pageType === 'achterdeur' ||
    pageType === 'terrasdeur' ||
    config.doorStyle !== undefined ||
    config.topLight !== undefined ||
    config.numberOfParts !== undefined ||
    config.designType !== undefined;

  if (isDoor) {
    const glassW = Math.max(0, width - FIXED_FRAME_OFFSET_MM);
    const glassH = Math.max(0, Math.min(height - FIXED_FRAME_OFFSET_MM, height - 313));
    return (glassW * glassH) / 1_000_000;
  }

  const parts = Number(config.parts) || 1;
  const partWidths = getPartWidths(config, parts, width);
  return calcConfiGoFixedGlassArea(partWidths, height, parts);
};

export const calcTiltTurn36WindowPrice = (widthMm, heightMm) => {
  const glassArea = glassAreaSqm(widthMm, heightMm, OPENING_FRAME_OFFSET_MM);
  return round2(
    OPENING_WINDOW_36.intercept + OPENING_WINDOW_36.glassAreaRate * glassArea
  );
};

export const calcTiltOnly36WindowPrice = (widthMm, heightMm) => {
  const glassArea = glassAreaSqm(widthMm, heightMm, OPENING_FRAME_OFFSET_MM);
  if (glassArea < OPENING_WINDOW_36.smallHopperGlassAreaThreshold) {
    return OPENING_WINDOW_36.smallHopperUnitPrice;
  }
  return round2(
    OPENING_WINDOW_36.intercept +
      OPENING_WINDOW_36.glassAreaRate * glassArea +
      OPENING_WINDOW_36.hopperPremium
  );
};

export const calcMixed2PartKozijnen36Price = (widthMm, heightMm) => {
  const outerAreaSqm = (widthMm * heightMm) / 1_000_000;
  const anchors = MIXED_2PART_KOZIJNEN_36_ANCHORS.map((a) => ({
    width: a.outerAreaSqm,
    price: a.price,
  }));
  return round2(interpolateAnchors(outerAreaSqm, anchors));
};

export const calcAchterdeur36Price = (widthMm, heightMm) =>
  round2(calcDoorPrice(widthMm, heightMm) + ACHTERDEUR_36_ADJUSTMENT);

export const calcVoordeur36Price = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;

  if (
    Math.abs(width - VOORDEUR_36_K_ANCHOR.width) <= 5 &&
    Math.abs(height - VOORDEUR_36_K_ANCHOR.height) <= 5
  ) {
    return { price: VOORDEUR_36_K_ANCHOR.price, hasPricing: true };
  }

  const base = calcVoordeurPrice(config);
  if (!base.hasPricing) return base;

  const glassArea = getConfiguratorGlazingAreaSqm(config, 'voordeur');
  return {
    price: round2(base.price + GLAZING_36_FIXED_PREMIUM_PER_SQM * glassArea),
    hasPricing: true,
  };
};

/** Voordeur glazing uplift disabled for now — selection has no price effect. */
export const calcVoordeurGlazingPremium = (_config) => 0;

// export const calcVoordeurGlazingPremium = (config) => {
//   if (!isGlazing36mm(config?.glazingType)) return 0;
//   const base24 = calcVoordeurPrice(config);
//   const base36 = calcVoordeur36Price(config);
//   if (!base24.hasPricing || !base36.hasPricing) return 0;
//   return round2(base36.price - base24.price);
// };

/** @deprecated Use product-specific 36 mm calculators; kept for diagnostics */

export const calcGlazing36Premium = (config, pageType) => {
  if (!isGlazing36mm(config?.glazingType)) return 0;
  const base24 = calculateConfiguratorPrice(
    { ...config, glazingType: 'double' },
    pageType
  );
  const base36 = calculateConfiguratorPrice(config, pageType);
  if (!base24.hasPricing || !base36.hasPricing) return 0;
  return round2(base36.price - base24.price);
};

const isOpeningWindowType = (type) =>
  type === 'tilt-turn-left' ||
  type === 'tilt-turn-right' ||
  type === 'tilt-only';

const isMixedFixedOpening2Part = (config, parts) => {
  if (parts !== 2) return false;
  const types = config.partWindowTypes || [];
  if (types.length < 2) return false;
  const fixedCount = types.filter((t) => isFixedWindowType(t)).length;
  const openingCount = types.filter((t) => isOpeningWindowType(t)).length;
  return fixedCount === 1 && openingCount === 1;
};

const getPartTypeCounts = (config, parts) => {
  const types = Array.from(
    { length: parts },
    (_, i) => config.partWindowTypes?.[i] || 'fixed-sash'
  );
  return {
    types,
    dk: types.filter(
      (t) => t === 'tilt-turn-left' || t === 'tilt-turn-right'
    ).length,
    hopper: types.filter((t) => t === 'tilt-only').length,
    panel: types.filter((t) => t === 'fixed-panel').length,
    allFixedGlass: types.every((t) => isFixedWindowType(t)),
  };
};

/**
 * Opening-hardware premiums over Conf Go all-glass baseline.
 * prijzen Config go 3.0.pdf (24 mm HR++ 5-15-4).
 */

const CONFIG30_MIXED_PREMIUM_REF = [
  {
    parts: 2,
    dk: 1,
    hopper: 0,
    panel: 0,
    height: 1000,
    premium: 138.1,
    refDkWidth: 1000,
    refHopperWidth: 0,
  },
  {
    parts: 2,
    dk: 2,
    hopper: 0,
    panel: 0,
    height: 1000,
    premium: 276.21,
    refDkWidth: 1000,
    refHopperWidth: 0,
  },
  {
    parts: 2,
    dk: 0,
    hopper: 1,
    panel: 0,
    height: 750,
    premium: 207.45,
    refDkWidth: 0,
    refHopperWidth: 650,
  },
  {
    parts: 2,
    dk: 0,
    hopper: 0,
    panel: 1,
    height: 1000,
    premium: 22.75,
    refDkWidth: 0,
    refHopperWidth: 0,
  },
  {
    parts: 3,
    dk: 1,
    hopper: 0,
    panel: 0,
    height: 1000,
    premium: 247.32,
    refDkWidth: 637,
    refHopperWidth: 0,
  },
  {
    parts: 4,
    dk: 2,
    hopper: 0,
    panel: 0,
    height: 1000,
    premium: 430.0,
    refDkWidth: 650,
    refHopperWidth: 0,
  },
  {
    parts: 4,
    dk: 1,
    hopper: 0,
    panel: 0,
    height: 1000,
    premium: 293.87,
    refDkWidth: 650,
    refHopperWidth: 0,
  },
];

const avgWidthForTypes = (types, widths, predicate) => {
  const selected = widths.filter((_, i) => predicate(types[i]));
  if (!selected.length) return 0;
  return selected.reduce((sum, w) => sum + w, 0) / selected.length;
};

const calcMixedOpeningPremium24 = (
  config,
  height,
  parts,
  partWidths,
  counts
) => {
  const { types, dk, hopper, panel } = counts;
  const ref = CONFIG30_MIXED_PREMIUM_REF.find(
    (row) =>
      row.parts === parts &&
      row.dk === dk &&
      row.hopper === hopper &&
      row.panel === panel
  );

  if (ref) {
    let premium = ref.premium * (height / ref.height);
    if (ref.refDkWidth > 0 && dk > 0) {
      const avgDk = avgWidthForTypes(
        types,
        partWidths,
        (t) => t === 'tilt-turn-left' || t === 'tilt-turn-right'
      );
      premium *= avgDk / ref.refDkWidth;
    }
    if (ref.refHopperWidth > 0 && hopper > 0) {
      const avgHopper = avgWidthForTypes(
        types,
        partWidths,
        (t) => t === 'tilt-only'
      );
      premium *= avgHopper / ref.refHopperWidth;
    }
    return round2(premium);
  }

  let premium = panel * 22.75;
  types.forEach((type, index) => {
    const partW = partWidths[index];
    const scale = (partW / 1000) * (height / 1000);
    if (type === 'tilt-turn-left' || type === 'tilt-turn-right') {
      premium += 138.1 * scale * (parts / 2);
    } else if (type === 'tilt-only') {
      premium += 207.45 * (partW / 650) * (height / 750);
    }
  });
  return round2(premium);
};

const getKozijnenPriceOpts = (config, width, height, parts) => ({
  parts,
  partWidths: getPartWidths(config, parts, width),
  ventCount: countFixedVentilators(config, parts),
  texturedGlass: hasTexturedGlass(config),
});

const calcFixedKozijnenUnitPrice = (
  width,
  height,
  priceOpts,
  glazing36,
  withVensterbank
) => {
  const parts = priceOpts.parts || 1;
  const partWidths =
    priceOpts.partWidths ||
    (parts === 1 ? [width] : getPartWidths({ partWidths: priceOpts.partWidths }, parts, width));

  if (withVensterbank) {
    const base = calcVensterbankFixedWindowPrice(width, height, {
      ...priceOpts,
      partWidths,
    });
    if (!glazing36) return base;
    const glassArea = calcConfiGoFixedGlassArea(partWidths, height, parts);
    return round2(base + GLAZING_36_FIXED_PREMIUM_PER_SQM * glassArea);
  }

  return glazing36
    ? calcConfiGoFixedWindow36Price(width, height, priceOpts)
    : calcConfiGoFixedWindowPrice(width, height, priceOpts);
};

const calcKozijnenMultiPartPrice = (config, width, height, parts, glazing36) => {
  const counts = getPartTypeCounts(config, parts);
  const priceOpts = getKozijnenPriceOpts(config, width, height, parts);
  const withVensterbank = hasVensterbank(config);

  if (counts.allFixedGlass && counts.panel === 0) {
    return {
      price: calcFixedKozijnenUnitPrice(
        width,
        height,
        priceOpts,
        glazing36,
        withVensterbank
      ),
      hasPricing: true,
    };
  }

  if (
    glazing36 &&
    parts === 2 &&
    counts.dk === 1 &&
    counts.hopper === 0 &&
    counts.panel === 0 &&
    isMixedFixedOpening2Part(config, parts)
  ) {
    return {
      price: calcMixed2PartKozijnen36Price(width, height),
      hasPricing: true,
    };
  }

  const base = calcFixedKozijnenUnitPrice(
    width,
    height,
    priceOpts,
    glazing36,
    withVensterbank
  );
  const openingPremium = calcMixedOpeningPremium24(
    config,
    height,
    parts,
    priceOpts.partWidths,
    counts
  );

  return { price: round2(base + openingPremium), hasPricing: true };
};

export const calcReferenceFixedWindowPrice = (widthMm, heightMm) =>
  calcVensterbankFixedWindowPrice(widthMm, heightMm);

/** Fixed glass with VensterbANK (reference quotation PDF). */

export const calcVensterbankFixedWindowPrice = (
  widthMm,
  heightMm,
  { parts = 1, partWidths = null, ventCount = 0, texturedGlass = false } = {}
) => {
  const widths =
    parts === 1
      ? [widthMm]
      : partWidths || getPartWidths({ partWidths }, parts, widthMm);

  const glassArea =
    parts === 1
      ? glassAreaSqm(widthMm, heightMm, FIXED_FRAME_OFFSET_MM)
      : calcConfiGoFixedGlassArea(widths, heightMm, parts);

  let price =
    REFERENCE_FIXED_WINDOW.intercept +
    REFERENCE_FIXED_WINDOW.glassAreaRate * glassArea;

  if (heightMm < LOW_HEIGHT_THRESHOLD_MM) {
    price +=
      REFERENCE_FIXED_WINDOW.lowHeightRate *
      ((LOW_HEIGHT_THRESHOLD_MM - heightMm) * widthMm) /
      1_000_000;
  }

  if (parts > 1) {
    price += CONFI_GO_FIXED_WINDOW.mullionPremium;
  }

  const ventRate =
    parts > 1
      ? CONFI_GO_FIXED_WINDOW.ventPremiumMultiPart
      : CONFI_GO_FIXED_WINDOW.ventPremiumSinglePart;
  price += ventCount * ventRate;

  if (texturedGlass) {
    price += CONFI_GO_FIXED_WINDOW.texturedGlassPremium;
  }

  return round2(price);
};

/** @deprecated Use calcVensterbankFixedWindowPrice */

export const calcRampFixedWindowPrice = calcVensterbankFixedWindowPrice;

export const calcConfiGoFixedWindowPrice = (
  widthMm,
  heightMm,
  { parts = 1, partWidths = null, ventCount = 0, texturedGlass = false } = {}
) => {
  const widths =
    parts === 1
      ? [widthMm]
      : partWidths || getPartWidths({ partWidths }, parts, widthMm);

  const glassArea = calcConfiGoFixedGlassArea(widths, heightMm, parts);
  let price =
    CONFI_GO_FIXED_WINDOW.intercept +
    CONFI_GO_FIXED_WINDOW.glassAreaRate * glassArea;

  if (parts > 1) {
    price += CONFI_GO_FIXED_WINDOW.mullionPremium;
  }

  const ventRate =
    parts > 1
      ? CONFI_GO_FIXED_WINDOW.ventPremiumMultiPart
      : CONFI_GO_FIXED_WINDOW.ventPremiumSinglePart;
  price += ventCount * ventRate;

  if (texturedGlass) {
    price += CONFI_GO_FIXED_WINDOW.texturedGlassPremium;
  }

  return round2(price);
};

export const calcConfiGoFixedWindow36Price = (
  widthMm,
  heightMm,
  options = {}
) => {
  const parts = options.parts || 1;
  const partWidths =
    parts === 1
      ? [widthMm]
      : options.partWidths ||
        getPartWidths({ partWidths: options.partWidths }, parts, widthMm);
  const base = calcConfiGoFixedWindowPrice(widthMm, heightMm, options);
  const glassArea = calcConfiGoFixedGlassArea(partWidths, heightMm, parts);
  return round2(base + GLAZING_36_FIXED_PREMIUM_PER_SQM * glassArea);
};

/** @deprecated Use calcConfiGoFixedWindowPrice for Config Go kozijnen pricing */

export const calcFixedWindowPrice = calcConfiGoFixedWindowPrice;

export const calcTiltTurnWindowPrice = (widthMm, heightMm) => {
  const glassArea = glassAreaSqm(widthMm, heightMm, OPENING_FRAME_OFFSET_MM);
  return round2(
    OPENING_WINDOW.tiltIntercept + OPENING_WINDOW.glassAreaRate * glassArea
  );
};

export const calcTiltOnlyWindowPrice = (widthMm, heightMm) => {
  return round2(
    calcTiltTurnWindowPrice(widthMm, heightMm) + OPENING_WINDOW.hopperPremium
  );
};

export const calcPlainTuindeurPrice = (widthMm, heightMm) => {
  const areaSqm = (widthMm * heightMm) / 1_000_000;
  return round2(PLAIN_TUINDEUR.intercept + PLAIN_TUINDEUR.areaRate * areaSqm);
};

const sidePanelUsesSandwich = (config, side) => {
  const sideParapet =
    side === 'left' ? config.leftPanelParapet : config.rightPanelParapet;
  const sideParapetPanelType =
    side === 'left'
      ? config.leftPanelParapetPanelType
      : config.rightPanelParapetPanelType;
  return Boolean(
    sideParapet && sideParapetPanelType === 'sandwich-no-mullions'
  );
};

/** Achterdeur "two parts" with solid lower section (PDF I/J). Glass lower stays plain layout. */

const achterdeurHasSolidLowerSection = (config) =>
  config.numberOfParts === 'two-parts' &&
  (config.panelType || 'sandwich-no-mullions') !== 'glass';

const hasTuindeurTopLight = (config, pageType) =>
  pageType === 'terrasdeur'
    ? config.doorType === 'top-light'
    : config.topLight === 'with-top-light';

const getTuindeurSidePanelWidths = (config) => {
  const designType = config.designType || 'no-panel';
  const totalWidth = Number(config.width) || 0;
  const hasLeft = designType === 'left-panel' || designType === 'both-panels';
  const hasRight = designType === 'right-panel' || designType === 'both-panels';
  const left =
    config.leftPanelWidth !== undefined
      ? Number(config.leftPanelWidth)
      : hasLeft
        ? hasRight
          ? Math.round(totalWidth * 0.25)
          : Math.round(totalWidth * 0.3)
        : 0;
  const right =
    config.rightPanelWidth !== undefined
      ? Number(config.rightPanelWidth)
      : hasRight
        ? hasLeft
          ? Math.round(totalWidth * 0.25)
          : Math.round(totalWidth * 0.3)
        : 0;
  return { left, right };
};

const scaleByHeight = (premium, heightMm) =>
  premium * (heightMm / TUINDEUR_REF_DOOR_HEIGHT_MM);

const calcTuindeurParapetPremium = (doorWidth, doorHeight, hasSidePanel) => {
  if (hasSidePanel) {
    return (
      TUINDEUR_PARAPET_WITH_SIDE_AT_REF *
      (doorWidth * doorHeight) /
      (TUINDEUR_PARAPET_WITH_SIDE_DOOR_REF.w *
        TUINDEUR_PARAPET_WITH_SIDE_DOOR_REF.h)
    );
  }
  return (
    TUINDEUR_PARAPET_PREMIUM_AT_REF *
    (doorWidth * doorHeight) /
    (TUINDEUR_DOOR_REF_WIDTH_MM.achterdeur * TUINDEUR_REF_DOOR_HEIGHT_MM)
  );
};

const calcTuindeurTopLightPremium = (
  outerWidth,
  overheadHeight,
  variant
) => {
  const ref = TUINDEUR_TOP_LIGHT[variant];
  if (!ref) return 0;
  return (
    ref.premium *
    (outerWidth / ref.refWidth) *
    (overheadHeight / 300)
  );
};

const getTuindeurTopLightVariant = (layout) => {
  const { hasParapet, hasLeft, hasRight, pageType, totalWidth } = layout;
  const terrasRef = TUINDEUR_DOOR_REF_WIDTH_MM.terrasdeur;
  if (hasParapet && !hasLeft && !hasRight) return 'parapet';
  if (hasLeft && hasRight) return 'bothSides';
  if (hasLeft || hasRight) {
    if (pageType === 'terrasdeur' && totalWidth >= terrasRef) {
      return 'withSideTerras';
    }
    return 'withSide';
  }
  return 'plain';
};

/**
 * Resolve door-leaf vs side-panel widths from buitenmaat (PDF page 11).
 * Terrasdeur side panels grow total width; door leaf stays at 2000 mm (item G).
 * Achterdeur side panels grow total width; door leaf stays at 1000 mm (item K).
 */

const resolveTuindeurGeometry = (config, pageType) => {
  const designType = config.designType || 'no-panel';
  const totalWidth = Number(config.width) || 0;
  const refDoor = TUINDEUR_DOOR_REF_WIDTH_MM[pageType] || 1000;
  const achterdeurRef = TUINDEUR_DOOR_REF_WIDTH_MM.achterdeur;
  const overheadHeight = Number(config.overheadHeight) || 300;
  const hasTopLight = hasTuindeurTopLight(config, pageType);
  const configured = getTuindeurSidePanelWidths(config);

  const hasLeft = designType === 'left-panel' || designType === 'both-panels';
  const hasRight = designType === 'right-panel' || designType === 'both-panels';
  const useCompactLayout =
    (hasLeft || hasRight) && totalWidth < refDoor;
  const doorRef = useCompactLayout ? achterdeurRef : refDoor;

  let configHeight = Number(config.height) || 0;
  let doorHeight = configHeight || TUINDEUR_REF_DOOR_HEIGHT_MM;
  if (hasTopLight && doorHeight >= 2450) {
    doorHeight = doorHeight - overheadHeight;
  }

  let doorWidth = totalWidth;
  let firstSideWidth = 0;
  let additionalSideWidth = 0;
  let leftSandwich = false;
  let rightSandwich = false;

  if (designType === 'no-panel') {
    doorWidth = totalWidth;
  } else if (designType === 'left-panel') {
    if (!useCompactLayout && pageType === 'terrasdeur') {
      doorWidth = refDoor;
      firstSideWidth =
        config.leftPanelWidth !== undefined
          ? configured.left
          : totalWidth - refDoor;
    } else {
      firstSideWidth =
        config.leftPanelWidth !== undefined
          ? configured.left
          : Math.max(0, totalWidth - doorRef);
      doorWidth = totalWidth - firstSideWidth;
      if (totalWidth === doorRef && firstSideWidth > 0) {
        doorWidth = doorRef;
      }
    }
    leftSandwich = sidePanelUsesSandwich(config, 'left');
  } else if (designType === 'right-panel') {
    if (!useCompactLayout && pageType === 'terrasdeur') {
      doorWidth = refDoor;
      additionalSideWidth =
        config.rightPanelWidth !== undefined
          ? configured.right
          : totalWidth - refDoor;
    } else {
      additionalSideWidth =
        config.rightPanelWidth !== undefined
          ? configured.right
          : Math.max(0, totalWidth - doorRef);
      doorWidth = totalWidth - additionalSideWidth;
      if (totalWidth === doorRef && additionalSideWidth > 0) {
        doorWidth = doorRef;
      }
    }
    rightSandwich = sidePanelUsesSandwich(config, 'right');
  } else if (designType === 'both-panels') {
    if (!useCompactLayout && pageType === 'terrasdeur') {
      doorWidth = refDoor;
      const sideTotal = Math.max(0, totalWidth - refDoor);
      if (
        config.leftPanelWidth === undefined &&
        config.rightPanelWidth === undefined
      ) {
        firstSideWidth = Math.round(
          sideTotal * TUINDEUR_TERRAS_BOTH_SIDE_LEFT_RATIO
        );
        additionalSideWidth = sideTotal - firstSideWidth;
      } else {
        firstSideWidth = configured.left;
        additionalSideWidth = configured.right;
      }
    } else {
      doorWidth = totalWidth - configured.left - configured.right;
      firstSideWidth = configured.left;
      additionalSideWidth = configured.right;
    }
    leftSandwich = sidePanelUsesSandwich(config, 'left');
    rightSandwich = sidePanelUsesSandwich(config, 'right');
  }

  const sidePricingHeight =
    hasTopLight &&
    hasLeft &&
    hasRight &&
    (pageType === 'achterdeur' || useCompactLayout)
      ? doorHeight + overheadHeight
      : doorHeight;

  let hasParapet;
  if (pageType === 'achterdeur') {
    hasParapet = achterdeurHasSolidLowerSection(config);
  } else {
    hasParapet = config.parapet === 'with-parapet';
  }

  return {
    totalWidth,
    doorWidth,
    doorHeight,
    sidePricingHeight,
    firstSideWidth,
    additionalSideWidth,
    hasLeft,
    hasRight,
    hasParapet,
    hasTopLight,
    overheadHeight,
    leftSandwich,
    rightSandwich,
    pageType,
    useCompactLayout,
  };
};

/**
 * Composed CONFIG-GO tuindeur / achterdeur price (24 mm HR++).
 * All PDF items A–N are exact sums of these terms.
 */

export const calcTuindeurComposedPrice = (config, pageType) => {
  const layout = resolveTuindeurGeometry(config, pageType);
  const {
    totalWidth,
    doorWidth,
    doorHeight,
    sidePricingHeight,
    firstSideWidth,
    additionalSideWidth,
    hasLeft,
    hasRight,
    hasParapet,
    hasTopLight,
    overheadHeight,
    leftSandwich,
    rightSandwich,
    useCompactLayout,
  } = layout;

  let price = calcPlainTuindeurPrice(doorWidth, doorHeight);

  if (hasParapet) {
    price += calcTuindeurParapetPremium(
      doorWidth,
      doorHeight,
      hasLeft || hasRight
    );
  }

  if (firstSideWidth > 0) {
    const firstSideRate = useCompactLayout
      ? TUINDEUR_FIRST_SIDE_GLASS_MM_RATE.achterdeur
      : TUINDEUR_FIRST_SIDE_GLASS_MM_RATE[pageType] ||
        TUINDEUR_FIRST_SIDE_GLASS_MM_RATE.achterdeur;
    price += scaleByHeight(
      firstSideRate * firstSideWidth,
      sidePricingHeight
    );
    if (leftSandwich) {
      price += scaleByHeight(
        TUINDEUR_SIDE_SANDWICH_MM_RATE * firstSideWidth,
        sidePricingHeight
      );
    }
  }

  if (additionalSideWidth > 0) {
    price += scaleByHeight(
      TUINDEUR_ADDITIONAL_SIDE_MM_RATE * additionalSideWidth,
      sidePricingHeight
    );
    if (rightSandwich) {
      price += scaleByHeight(
        TUINDEUR_SIDE_SANDWICH_MM_RATE * additionalSideWidth,
        sidePricingHeight
      );
    }
  }

  if (hasTopLight) {
    if (hasLeft && hasRight && (pageType === 'achterdeur' || useCompactLayout)) {
      price +=
        calcPlainTuindeurPrice(totalWidth, doorHeight + overheadHeight) -
        calcPlainTuindeurPrice(totalWidth, doorHeight);
      price += calcTuindeurTopLightPremium(
        totalWidth,
        overheadHeight,
        'withSide'
      );
    } else {
      price += calcTuindeurTopLightPremium(
        totalWidth,
        overheadHeight,
        getTuindeurTopLightVariant(layout)
      );
    }
  }

  return round2(price);
};

/** @deprecated Use layout key for debugging */

export const getTuindeurLayoutProfile = (config, pageType) => {
  const designType = config.designType || 'no-panel';
  const hasTopLight =
    pageType === 'terrasdeur'
      ? config.doorType === 'top-light'
      : config.topLight === 'with-top-light';

  let hasParapet;
  if (pageType === 'achterdeur') {
    hasParapet = achterdeurHasSolidLowerSection(config);
  } else {
    hasParapet = config.parapet === 'with-parapet';
  }

  if (designType === 'no-panel') {
    if (hasParapet && hasTopLight) return 'plain-top-parapet';
    if (hasTopLight) return 'plain-top';
    if (hasParapet) return 'plain-parapet';
    return 'plain';
  }

  if (designType === 'left-panel' || designType === 'right-panel') {
    const side = designType === 'right-panel' ? 'right' : 'left';
    if (hasTopLight) return 'left-top';
    // Terrasdeur only — main door boswering with side panels (PDF H)
    if (pageType !== 'achterdeur' && config.parapet === 'with-parapet') {
      return 'both-parapet';
    }
    if (sidePanelUsesSandwich(config, side)) {
      const sandwichAnchor = TUINDEUR_PROFILE_ANCHORS['left-sandwich'][0];
      const w = Number(config.width) || 0;
      const h = Number(config.height) || 0;
      // PDF item B — sandwich zijpaneel only at 2200×2200; other sizes use glass-side (K/L/E)
      if (w === sandwichAnchor.width && h === sandwichAnchor.height) {
        return 'left-sandwich';
      }
    }
    return 'left';
  }

  if (hasParapet) return 'both-parapet';
  if (hasTopLight) return 'both-top';
  return 'both-glass';
};

/** Terrasdeur & achterdeur — composed formula from CONFIG-GO PDF premiums (24 mm). */

export const calcTuindeurAchterdeurPrice = (config, pageType) => {
  const layout = resolveTuindeurGeometry(config, pageType);

  if (layout.totalWidth <= 0 || layout.doorHeight <= 0) {
    return { price: 0, hasPricing: false, reason: 'invalid-dimensions' };
  }

  if (isGlazing36mm(config.glazingType)) {
    return { price: 0, hasPricing: false, reason: 'tuindeur-36mm-not-in-pdf' };
  }

  const textureId = config.glassTexture;
  if (
    textureId &&
    textureId !== 'helder-glas' &&
    textureId !== 'clear'
  ) {
    return { price: 0, hasPricing: false, reason: 'tuindeur-texture-not-in-pdf' };
  }

  return {
    price: calcTuindeurComposedPrice(config, pageType),
    hasPricing: true,
  };
};

export const calcDoorPrice = (widthMm, heightMm) => {
  const areaSqm = (widthMm * heightMm) / 1_000_000;
  return round2(DOOR.intercept + DOOR.areaRate * areaSqm);
};

const calcVoordeurBasePlain = (doorWidthMm, doorHeightMm) =>
  round2(VOORDEUR_FLAT_BASE_PRICE);

const resolveVoordeurGeometry = (config) => {
  const designType = config.designType || 'no-panel';
  const totalWidth = Number(config.width) || 0;
  const overheadHeight = Number(config.overheadHeight) || 300;
  const hasTopLight = config.doorType === 'top-light';

  let configHeight = Number(config.height) || 0;
  let doorHeight = configHeight || VOORDEUR_REF_DOOR_HEIGHT_MM;
  if (hasTopLight && doorHeight >= 2450) {
    doorHeight = doorHeight - overheadHeight;
  }

  let doorWidth = totalWidth;
  let firstSideWidth = 0;
  let additionalSideWidth = 0;

  if (designType === 'left-panel') {
    firstSideWidth =
      config.leftPanelWidth !== undefined
        ? Number(config.leftPanelWidth) || 0
        : Math.max(0, totalWidth - VOORDEUR_DOOR_REF_WIDTH_MM);
    doorWidth = totalWidth - firstSideWidth;
    if (totalWidth === VOORDEUR_DOOR_REF_WIDTH_MM && firstSideWidth > 0) {
      doorWidth = VOORDEUR_DOOR_REF_WIDTH_MM;
    }
  } else if (designType === 'right-panel') {
    additionalSideWidth =
      config.rightPanelWidth !== undefined
        ? Number(config.rightPanelWidth) || 0
        : Math.max(0, totalWidth - VOORDEUR_DOOR_REF_WIDTH_MM);
    doorWidth = totalWidth - additionalSideWidth;
    if (totalWidth === VOORDEUR_DOOR_REF_WIDTH_MM && additionalSideWidth > 0) {
      doorWidth = VOORDEUR_DOOR_REF_WIDTH_MM;
    }
  } else if (designType === 'both-panels') {
    firstSideWidth = Number(resolveSidePanelWidth(config, 'left')) || 0;
    additionalSideWidth = Number(resolveSidePanelWidth(config, 'right')) || 0;
    doorWidth = Math.max(0, totalWidth - firstSideWidth - additionalSideWidth);
  }

  return {
    totalWidth,
    doorWidth,
    doorHeight,
    configHeight,
    firstSideWidth,
    additionalSideWidth,
    hasTopLight,
    designType,
  };
};

const calcVoordeurTopLightTierPremium = (overheadHeightMm) =>
  lookupTierPremium(overheadHeightMm, VOORDEUR_TOP_LIGHT_TIERS);

const calcVoordeurSideTierPremium = (sideWidthMm) =>
  lookupTierPremium(sideWidthMm, VOORDEUR_SIDE_PANEL_TIERS);

/** Side premium: one tier lookup per panel; both-panels sums left + right. */
const calcVoordeurSideLayoutPremium = (config, layout) => {
  if (!voordeurHasSideDesign(layout.designType)) return 0;

  if (layout.designType === 'both-panels') {
    const leftWidth = Number(resolveSidePanelWidth(config, 'left')) || 0;
    const rightWidth = Number(resolveSidePanelWidth(config, 'right')) || 0;
    return calcVoordeurSideTierPremium(leftWidth) + calcVoordeurSideTierPremium(rightWidth);
  }

  const sideWidthMm = layout.firstSideWidth + layout.additionalSideWidth;
  return calcVoordeurSideTierPremium(sideWidthMm);
};

const calcVoordeurLayoutPremiums = (config) => {
  const layout = resolveVoordeurGeometry(config);
  const hasSide = voordeurHasSideDesign(layout.designType);
  const hasTop = config.doorType === 'top-light';
  const overheadHeightMm = Number(config.overheadHeight) || 300;

  const topPremium = hasTop ? calcVoordeurTopLightTierPremium(overheadHeightMm) : 0;
  const sidePremium = calcVoordeurSideLayoutPremium(config, layout);
  const comboSurcharge =
    hasSide && hasTop && layout.totalWidth > VOORDEUR_COMBO_WIDE_WIDTH_MM
      ? VOORDEUR_COMBO_SURCHARGE
      : 0;

  return { layout, hasSide, hasTop, topPremium, sidePremium, comboSurcharge };
};

/** Premium for tilt-turn-left, tilt-turn-right, or tilt-only on side panel and/or top light. */
const calcVoordeurOpeningWindowPremium = (config) => {
  const designType = config.designType || 'no-panel';
  const hasSide = voordeurHasSideDesign(designType);
  const hasTop = config.doorType === 'top-light';

  let openingOnSide = false;
  if (hasSide) {
    if (
      (designType === 'left-panel' || designType === 'both-panels') &&
      isOpeningWindowType(config.leftPanelWindowType)
    ) {
      openingOnSide = true;
    }
    if (
      (designType === 'right-panel' || designType === 'both-panels') &&
      isOpeningWindowType(config.rightPanelWindowType)
    ) {
      openingOnSide = true;
    }
  }

  let openingOnTop = false;
  if (hasTop) {
    const overheadTypes = config.overheadPartWindowTypes || [];
    openingOnTop = overheadTypes.some((type) => isOpeningWindowType(type));
  }

  if (openingOnSide && openingOnTop) {
    return VOORDEUR_OPENING_WINDOW_BOTH_ZONES_PREMIUM;
  }
  if (openingOnSide || openingOnTop) {
    return VOORDEUR_OPENING_WINDOW_PREMIUM;
  }
  return 0;
};

/** Map achterdeur config fields to the shared door-layout pricing shape (voordeur uses doorType). */
const mapAchterdeurPricingConfig = (config) => ({
  ...config,
  doorType: config.topLight === 'with-top-light' ? 'top-light' : 'normal',
});

const calcDoorLayoutRecreationPrice = (config, flatBasePrice) => {
  const { topPremium, sidePremium, comboSurcharge } = calcVoordeurLayoutPremiums(config);
  const openingPremium = calcVoordeurOpeningWindowPremium(config);
  return round2(
    flatBasePrice + topPremium + sidePremium + comboSurcharge + openingPremium
  );
};

/** Flat base + recreation premiums (top light, side panel, wide combo, opening windows). */
const calcVoordeurRecreationPrice = (config) =>
  calcDoorLayoutRecreationPrice(config, VOORDEUR_FLAT_BASE_PRICE);

const calcAchterdeurTwoPartsPremium = (config) =>
  countAchterdeurTwoPartsParapetSections(config) * DOOR_SANDWICH_SECTION_PREMIUM;

const calcTerrasdeurSandwichPremium = (config) =>
  calcTerrasdeurSandwichParapetPremium(config);

const calcAchterdeurRecreationPrice = (config) =>
  round2(
    calcDoorLayoutRecreationPrice(mapAchterdeurPricingConfig(config), ACHTERDEUR_FLAT_BASE_PRICE) +
      calcAchterdeurTwoPartsPremium(config)
  );

const calcTerrasdeurRecreationPrice = (config) =>
  round2(
    calcDoorLayoutRecreationPrice(config, TERRASDEUR_FLAT_BASE_PRICE) +
      calcTerrasdeurSandwichPremium(config) +
      calcTerrasdeurSidePanelOverheadPremium(config)
  );

const calcVoordeurSidePremium = (sideWidthMm) => calcVoordeurSideTierPremium(sideWidthMm);

const voordeurHasSideDesign = (designType) =>
  designType === 'left-panel' ||
  designType === 'right-panel' ||
  designType === 'both-panels';

/**
 * Door subtotal without top-light premium (base + side panel + combo when applicable).
 * Kept for breakdown helpers that split top light from the rest.
 */
const calcVoordeurComposedPrice = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;
  if (width <= 0 || height <= 0) return null;

  const premiums = calcVoordeurLayoutPremiums({ ...config, doorType: 'normal' });
  const comboSurcharge =
    premiums.hasSide && config.doorType === 'top-light' && premiums.layout.totalWidth > VOORDEUR_COMBO_WIDE_WIDTH_MM
      ? VOORDEUR_COMBO_SURCHARGE
      : 0;

  return round2(VOORDEUR_FLAT_BASE_PRICE + premiums.sidePremium + comboSurcharge);
};

const calcVoordeurTopLightPrice = (config) => calcVoordeurRecreationPrice(config);

const resolveStyle6Layout = (config) => {
  if ((config.doorStyle || 'style-4') !== 'style-6') {
    return config.style6Layout;
  }
  if (config.style6Layout === 'k' || config.style6Layout === 'm') {
    return config.style6Layout;
  }
  return Number(config.numberOfParts) === 4 ? 'k' : 'm';
};

const resolveVoordeurStylePremium = (config) => {
  const doorStyle = config.doorStyle || 'style-4';
  if (doorStyle === 'style-6') {
    return resolveStyle6Layout(config) === 'k'
      ? VOORDEUR_STYLE6_K_PREMIUM_AT_REF
      : VOORDEUR_STYLE6_M_PREMIUM_AT_REF;
  }
  const premium = VOORDEUR_STYLE_PREMIUM_AT_REF[doorStyle];
  return premium === null ? null : premium;
};

export const deurUsesHvl = (config) => {
  const joint = config?.woodJoint ?? 'joint-45';
  return joint === 'joint-90' || config?.hvlSurcharge === true;
};

export const voordeurUsesHvl = deurUsesHvl;

/** Additive HVL premium when woodJoint is 90° (flat per door drawing, incl. BTW). */
export const calcVoordeurWoodJointPremium = (config) =>
  deurUsesHvl(config) ? DOOR_HVL_SURCHARGE_INCL_VAT : 0;

const calcDeurCatalogPriceExVat = (recreationInclVat, config) =>
  round2(
    normalizeDoorCatalogPriceToExVat(
      Number(recreationInclVat) + calcVoordeurWoodJointPremium(config)
    )
  );

/**
 * Price lines for voordeur — amounts sum to calcVoordeurPrice (HVL listed separately).
 * @returns {ReadonlyArray<{ key: string, amount: number, designType?: string }>}
 */

export const getVoordeurPriceBreakdown = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;
  if (width <= 0 || height <= 0) return [];

  const { layout, hasSide, hasTop, topPremium, sidePremium, comboSurcharge } =
    calcVoordeurLayoutPremiums(config);

  const lines = [{ key: 'doorBase', amount: VOORDEUR_FLAT_BASE_PRICE }];

  if (hasTop && topPremium > 0) {
    lines.push({ key: 'topLight', amount: topPremium });
  }

  if (hasSide && sidePremium > 0) {
    lines.push({
      key: 'sidePanel',
      amount: sidePremium,
      designType: layout.designType,
    });
  }

  if (comboSurcharge > 0) {
    lines.push({ key: 'comboWideSurcharge', amount: comboSurcharge });
  }

  const openingPremium = calcVoordeurOpeningWindowPremium(config);
  if (openingPremium > 0) {
    lines.push({ key: 'openingWindow', amount: openingPremium });
  }

  const hvl = calcVoordeurWoodJointPremium(config);
  if (hvl > 0) {
    lines.push({
      key: 'hvl',
      amount: hvl,
    });
  }

  return lines;
};

export const getAchterdeurPriceBreakdown = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;
  if (width <= 0 || height <= 0) return [];

  const pricingConfig = mapAchterdeurPricingConfig(config);
  const { layout, hasSide, hasTop, topPremium, sidePremium, comboSurcharge } =
    calcVoordeurLayoutPremiums(pricingConfig);

  const lines = [{ key: 'doorBase', amount: ACHTERDEUR_FLAT_BASE_PRICE }];

  if (hasTop && topPremium > 0) {
    lines.push({ key: 'topLight', amount: topPremium });
  }

  if (hasSide && sidePremium > 0) {
    lines.push({
      key: 'sidePanel',
      amount: sidePremium,
      designType: layout.designType,
    });
  }

  if (comboSurcharge > 0) {
    lines.push({ key: 'comboWideSurcharge', amount: comboSurcharge });
  }

  const openingPremium = calcVoordeurOpeningWindowPremium(pricingConfig);
  if (openingPremium > 0) {
    lines.push({ key: 'openingWindow', amount: openingPremium });
  }

  const twoPartsPremium = calcAchterdeurTwoPartsPremium(config);
  if (twoPartsPremium > 0) {
    lines.push({ key: 'twoParts', amount: twoPartsPremium });
  }

  const hvl = calcVoordeurWoodJointPremium(config);
  if (hvl > 0) {
    lines.push({ key: 'hvl', amount: hvl });
  }

  return lines;
};

export const getTerrasdeurPriceBreakdown = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;
  if (width <= 0 || height <= 0) return [];

  const { layout, hasSide, hasTop, topPremium, sidePremium, comboSurcharge } =
    calcVoordeurLayoutPremiums(config);

  const lines = [{ key: 'doorBase', amount: TERRASDEUR_FLAT_BASE_PRICE }];

  if (hasTop && topPremium > 0) {
    lines.push({ key: 'topLight', amount: topPremium });
  }

  if (hasSide && sidePremium > 0) {
    lines.push({
      key: 'sidePanel',
      amount: sidePremium,
      designType: layout.designType,
    });
  }

  if (comboSurcharge > 0) {
    lines.push({ key: 'comboWideSurcharge', amount: comboSurcharge });
  }

  const openingPremium = calcVoordeurOpeningWindowPremium(config);
  if (openingPremium > 0) {
    lines.push({ key: 'openingWindow', amount: openingPremium });
  }

  const sandwichPremium = calcTerrasdeurSandwichPremium(config);
  if (sandwichPremium > 0) {
    lines.push({ key: 'sandwichParapet', amount: sandwichPremium });
  }

  const hvl = calcVoordeurWoodJointPremium(config);
  if (hvl > 0) {
    lines.push({ key: 'hvl', amount: hvl });
  }

  return lines;
};

export const calcAchterdeurPrice = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;

  if (width <= 0 || height <= 0) {
    return { price: 0, hasPricing: false, reason: 'invalid-dimensions' };
  }

  return {
    price: calcDeurCatalogPriceExVat(calcAchterdeurRecreationPrice(config), config),
    hasPricing: true,
  };
};

/** Keralit cladding — €350 per m² (width × height in mm). */
export const KERALIT_PRICE_PER_SQM = 350;

export const KERALIT_MIN_DIMENSION_MM = 500;
/** Max single side (mm) — supports square panels up to 150 m². */
export const KERALIT_MAX_DIMENSION_MM = 12500;
export const KERALIT_MIN_AREA_SQM =
  (KERALIT_MIN_DIMENSION_MM * KERALIT_MIN_DIMENSION_MM) / 1_000_000;
export const KERALIT_MAX_AREA_SQM = 150;

export const calcKeralitAreaSqm = (widthMm, heightMm) => {
  const width = Number(widthMm) || 0;
  const height = Number(heightMm) || 0;
  if (width <= 0 || height <= 0) return 0;
  return (width * height) / 1_000_000;
};

/** Scale width/height to match target m² while preserving aspect ratio. */
export const calcKeralitDimensionsFromAreaSqm = (
  targetAreaSqm,
  currentWidthMm,
  currentHeightMm,
  {
    minDimension = KERALIT_MIN_DIMENSION_MM,
    maxDimension = KERALIT_MAX_DIMENSION_MM,
  } = {},
) => {
  const target = Number(targetAreaSqm);
  const width = Number(currentWidthMm) || minDimension;
  const height = Number(currentHeightMm) || minDimension;

  if (!Number.isFinite(target) || target <= 0) {
    return { width, height, valid: false };
  }

  const minArea = KERALIT_MIN_AREA_SQM;
  const clampedTarget = Math.min(Math.max(target, minArea), KERALIT_MAX_AREA_SQM);

  const currentArea = calcKeralitAreaSqm(width, height);
  let newWidth;
  let newHeight;

  if (currentArea > 0) {
    const scale = Math.sqrt(clampedTarget / currentArea);
    newWidth = Math.round(width * scale);
    newHeight = Math.round(height * scale);
  } else {
    const side = Math.round(Math.sqrt(clampedTarget * 1_000_000));
    newWidth = side;
    newHeight = side;
  }

  newWidth = Math.min(Math.max(newWidth, minDimension), maxDimension);
  newHeight = Math.min(Math.max(newHeight, minDimension), maxDimension);

  return {
    width: newWidth,
    height: newHeight,
    valid: true,
    areaSqm: calcKeralitAreaSqm(newWidth, newHeight),
  };
};

export const calcKeralitPrice = (config) => {
  const areaSqm = calcKeralitAreaSqm(config?.width, config?.height);
  if (areaSqm <= 0) {
    return { price: 0, hasPricing: false, reason: 'invalid-dimensions' };
  }
  return {
    price: round2(areaSqm * KERALIT_PRICE_PER_SQM),
    hasPricing: true,
  };
};

export const isKeralitConfig = (config) =>
  config?.lineSpacing !== undefined &&
  config?.lineType !== undefined &&
  config?.parts === undefined &&
  config?.profileSystem === undefined &&
  config?.doorStyle === undefined &&
  config?.designType === undefined &&
  config?.slidingPosition === undefined;

/** Schuifpui (sliding door) — base @ 2000×2000 mm, +€100 per 100 mm over 2 m per dimension */
export const SCHUIFPUI_BASE_WIDTH_MM = 2000;
export const SCHUIFPUI_BASE_HEIGHT_MM = 2000;
export const SCHUIFPUI_MIN_WIDTH_BY_POSITION = {
  left: 2000,
  right: 3000,
  center: 4000,
};
/** @deprecated Use getSchuifpuiMinWidthMm(slidingPosition) */
export const SCHUIFPUI_MIN_WIDTH_MM = SCHUIFPUI_MIN_WIDTH_BY_POSITION.left;
export const SCHUIFPUI_MIN_HEIGHT_MM = 1800;
export const SCHUIFPUI_MAX_HEIGHT_MM = 2500;
export const SCHUIFPUI_MAX_WIDTH_MM = 6000;
export const SCHUIFPUI_DIMENSION_STEP_MM = 100;
export const SCHUIFPUI_DIMENSION_STEP_PRICE = 100;

export const getSchuifpuiMinWidthMm = (slidingPosition) =>
  SCHUIFPUI_MIN_WIDTH_BY_POSITION[slidingPosition] ??
  SCHUIFPUI_MIN_WIDTH_BY_POSITION.left;

export const clampSchuifpuiDimensions = (config) => {
  const slidingPosition = config.slidingPosition || 'left';
  const minWidth = getSchuifpuiMinWidthMm(slidingPosition);
  const width = Math.max(
    minWidth,
    Math.min(SCHUIFPUI_MAX_WIDTH_MM, Math.round(Number(config.width) || minWidth))
  );
  const height = Math.max(
    SCHUIFPUI_MIN_HEIGHT_MM,
    Math.min(SCHUIFPUI_MAX_HEIGHT_MM, Math.round(Number(config.height) || SCHUIFPUI_MIN_HEIGHT_MM))
  );
  return { ...config, slidingPosition, width, height };
};

/** Catalog incl. 21% BTW — ex-VAT returned from calcSchuifpuiPrice */
export const SCHUIFPUI_SLIDING_BASE_PRICES = {
  left: 7300,
  right: 8500,
  center: 10000,
};

export const calcSchuifpuiDimensionSurcharge = (widthMm, heightMm) => {
  const widthExcess = Math.max(0, Number(widthMm) - SCHUIFPUI_BASE_WIDTH_MM);
  const heightExcess = Math.max(0, Number(heightMm) - SCHUIFPUI_BASE_HEIGHT_MM);
  const widthSurcharge =
    (widthExcess / SCHUIFPUI_DIMENSION_STEP_MM) * SCHUIFPUI_DIMENSION_STEP_PRICE;
  const heightSurcharge =
    (heightExcess / SCHUIFPUI_DIMENSION_STEP_MM) * SCHUIFPUI_DIMENSION_STEP_PRICE;
  return widthSurcharge + heightSurcharge;
};

export const SCHUIFPUI_VENTILATOR_PRICE_PER_100MM = 10;

/**
 * Horizontal span used for ventilator pricing — one sash share of door width (matches ventilator bar on drawing).
 * Uses config.width only; height never affects this.
 */
export const getSchuifpuiVentilatorPricingWidthMm = (config) => {
  const width = Math.max(0, Number(config.width) || 0);
  const position = config.slidingPosition || 'left';
  const frameCount = position === 'center' ? 4 : 2;
  return frameCount > 0 ? width / frameCount : 0;
};

export const calcSchuifpuiVentilatorFramePrice = (widthMm) => {
  const w = Math.max(0, Number(widthMm) || 0);
  return round2((w / 100) * SCHUIFPUI_VENTILATOR_PRICE_PER_100MM);
};

const SCHUIFPUI_VENTILATOR_KEYS_BY_POSITION = {
  left: ['left', 'right'],
  right: ['left', 'right'],
  center: ['centerLeft', 'centerRight', 'centerCenter', 'centerThird'],
};

export const getSchuifpuiActiveVentilatorKeys = (config) => {
  const ventilators = config.ventilatorSliding || {};
  const position = config.slidingPosition || 'left';
  const keys = SCHUIFPUI_VENTILATOR_KEYS_BY_POSITION[position] || SCHUIFPUI_VENTILATOR_KEYS_BY_POSITION.left;
  return keys.filter((key) => Boolean(ventilators[key]));
};

/** €10 per 100 mm of sash width, per frame with ventilator. Height does not affect this. */
export const calcSchuifpuiVentilatorTotal = (config) => {
  const activeKeys = getSchuifpuiActiveVentilatorKeys(config);
  if (activeKeys.length === 0) return 0;
  const sashWidthMm = getSchuifpuiVentilatorPricingWidthMm(config);
  const unitPrice = calcSchuifpuiVentilatorFramePrice(sashWidthMm);
  return round2(activeKeys.length * unitPrice);
};

export const calcSchuifpuiUnitPrice = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;
  const position = config.slidingPosition || 'left';
  const base =
    SCHUIFPUI_SLIDING_BASE_PRICES[position] ?? SCHUIFPUI_SLIDING_BASE_PRICES.left;
  const inclVat =
    base + calcSchuifpuiDimensionSurcharge(width, height) + calcSchuifpuiVentilatorTotal(config);
  return normalizeDoorCatalogPriceToExVat(inclVat);
};

export const calcSchuifpuiPrice = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;

  if (width <= 0 || height <= 0) {
    return { price: 0, hasPricing: false, reason: 'invalid-dimensions' };
  }

  return {
    price: round2(calcSchuifpuiUnitPrice(config)),
    hasPricing: true,
  };
};

export const calcTerrasdeurPrice = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;

  if (width <= 0 || height <= 0) {
    return { price: 0, hasPricing: false, reason: 'invalid-dimensions' };
  }

  return {
    price: normalizeDoorCatalogPriceToExVat(calcTerrasdeurRecreationPrice(config)),
    hasPricing: true,
  };
};

export const calcVoordeurPrice = (config) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;

  if (width <= 0 || height <= 0) {
    return { price: 0, hasPricing: false, reason: 'invalid-dimensions' };
  }

  return {
    price: calcDeurCatalogPriceExVat(calcVoordeurRecreationPrice(config), config),
    hasPricing: true,
  };
};

const getPrimaryWindowType = (config) =>
  config.partWindowTypes?.[0] || config.raamtype || 'fixed-sash';

const isKozijnenPage = (pageType, config) =>
  (pageType === 'kozijnen' || pageType === undefined) && !config.slidingPosition;

/**
 * Calculate unit price from PDF-derived formulas.
 * @returns {{ price: number, hasPricing: boolean, reason?: string }}
 */

export const calculateConfiguratorPrice = (config, pageType) => {
  const width = Number(config.width) || 0;
  const height = Number(config.height) || 0;

  if (width <= 0 || height <= 0) {
    return { price: 0, hasPricing: false, reason: 'invalid-dimensions' };
  }

  if (isKozijnenPage(pageType, config)) {
    const parts = Number(config.parts) || 1;

    if (parts > 5) {
      return { price: 0, hasPricing: false, reason: 'too-many-parts' };
    }

    return calcKozijnenAreaPrice(width, height, config);
  }

  if (pageType === 'voordeur') {
    // Glazing type pricing disabled for voordeur for now (24 mm and 36 mm same price).
    return calcVoordeurPrice(config);
  }

  if (pageType === 'achterdeur') {
    // Same layout premiums as voordeur (€3700 base); legacy tuindeur PDF formula not used.
    // Glazing 36 mm uplift disabled for now.
    return calcAchterdeurPrice(config);
  }

  if (pageType === 'terrasdeur') {
    return calcTerrasdeurPrice(config);
  }

  if (pageType === 'keralit' || isKeralitConfig(config)) {
    return calcKeralitPrice(config);
  }

  if (pageType === 'schuifpui') {
    return calcSchuifpuiPrice(config);
  }

  return { price: 0, hasPricing: false, reason: 'unsupported-product' };
};
